import type { Agent } from '@/types/domain'
import { http } from '@/platform/http'
import { uploadFile } from '@/platform/upload'

export interface TtsModel {
  id: string
  modelName: string
  isEnabled?: number
  modelCode?: string
}

export interface ModelOption {
  id: string
  modelName: string
}

export interface VoiceItem {
  id: string
  name?: string
  ttsVoice?: string
  voiceDemo?: string
  demoUrl?: string
  previewUrl?: string
}

export interface CloneModel {
  id: string
  name: string
  code: string
  provider: string
}

export const agentApi = {
  list: () => http.get<Agent[]>('/agent/list', { toastError: false }),
  detail: (id: string) => http.get<Agent>(`/agent/${id}`, { toastError: false }),
  create: (data: Partial<Agent>) => http.post<string>('/agent', data),
  update: (id: string, data: Partial<Agent>) => http.put<void>(`/agent/${id}`, data),
  remove: (id: string) => http.delete<void>(`/agent/${id}`),
  copy: (id: string) => http.post<string>(`/agent/${id}/copy`),
  uploadAvatar: (id: string, file: File) => uploadFile<{ url?: string } | string>(`/agent/${id}/avatar`, file),
  deleteAvatar: (id: string) => http.delete<void>(`/agent/${id}/avatar`),
  ttsModels: () => http.get<{ list?: TtsModel[], records?: TtsModel[] }>('/models/list', {
    params: { modelType: 'TTS', page: 0, limit: 100, viewAll: true },
    toastError: false,
  }),
  modelOptions: (modelType: string, modelName = '') => http.get<ModelOption[]>('/models/names', {
    params: { modelType, modelName },
    toastError: false,
  }),
  generatePrompt: (id: string, agentName: string) => http.post<string>(`/agent/${id}/generate-prompt`, { agentName }, { toastError: false }),
  ttsVoices: (modelId: string, voiceName = '') => http.get<VoiceItem[]>(`/models/${modelId}/voices`, {
    params: { voiceName },
    toastError: false,
  }),
  clonedVoices: (modelId: string) => http.get<VoiceItem[]>('/ttsVoice/clone/list', {
    params: { ttsModelId: modelId },
    toastError: false,
  }),
  cloneModels: () => http.get<CloneModel[]>('/ttsVoice/clone/models', { toastError: false }),
  cloneSupport: (modelId: string) => http.get<{ supported: boolean }>(`/ttsVoice/clone/check/${modelId}`, { toastError: false }),
  cloneVoice: (file: File, data: { ttsModelId: string, voiceName: string, referenceText: string, remark?: string }) =>
    // 克隆耗时可能较长，超时放宽到 60s（对齐旧版）
    uploadFile<{ voiceId: string, voiceName: string, message: string }>('/ttsVoice/clone/upload', file, 'audio', {
      ttsModelId: data.ttsModelId,
      voiceName: data.voiceName,
      referenceText: data.referenceText,
      remark: data.remark || '',
    }, 60000),
  transcribeAudio: (file: File) => uploadFile<{ text: string }>('/asr/transcribe', file, 'audio'),
  deleteVoice: (voiceId: string) => http.post<void>('/ttsVoice/delete', [voiceId]),
}
